import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';
import { Box, Button, DialogContent, Typography } from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { Show } from '../../../../../../../../../components/Show';
import { CardContainer } from './shared/StyledCardContainer';
import { AddItemDialogActions } from './shared/AddItemDialogActions';
import { ControlledTextField } from './shared/common';

export const WaverOfSubrogationModal = ({
  data: modalProps,
  close,
}: WaiverOfSubrogationModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<WaiverOfSubrogationFormValues>({
    defaultValues: {
      waivers: modalProps.waivers,
    },
    resolver: yupResolver(schema),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'waivers',
  });

  const handleAddExclusion = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      name: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one entity exists. If so, dont add a card by default */
    if (formMethods.getValues().waivers[0]?.name) {
      return;
    }

    handleAddExclusion({ scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Waivers of Subrogation
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <ControlledTextField
                  label="Business Name"
                  required
                  name={`waivers.${index}.name`}
                />

                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <AddItemDialogActions
          addLabel="Add Waiver"
          onAdd={() => handleAddExclusion({ scroll: true })}
          onCancel={close}
        />
      </form>
    </FormProvider>
  );
};

export default WaverOfSubrogationModal;

//types
type WaiverOfSubrogationModalProps = {
  data: {
    onSubmit: (formValues: WaiverOfSubrogationFormValues) => void;
    waivers: WaiverOfSubrogation[];
  };
  close: () => void;
};

export type WaiverOfSubrogationFormValues = {
  waivers: WaiverOfSubrogation[];
};

export type WaiverOfSubrogation = {
  name: string;
};

//validations
const schema = Yup.object().shape({
  waivers: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().label('Business name').required(),
    })
  ),
});

export const WaverOfSubrogationModalConfig = {
  WaverOfSubrogationModal: {
    component: WaverOfSubrogationModal,
    config: {
      title: 'Waiver of Subrogation',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};
